import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import SearchBar from "@/components/feature/SearchBar";
import ContentListItem from "@/components/feature/ContentListItem";
import VideoCard from "@/components/feature/VideoCard";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { contentItems } from "@/mocks/content";
import { homeHotWords } from "@/mocks/hotsearch";

const channels = [
  { to: "/news", icon: "ri-newspaper-line", title: "新闻", desc: "国内外最新资讯与时事", tone: "primary" },
  { to: "/video", icon: "ri-play-circle-line", title: "视频", desc: "精选短视频与科普内容", tone: "secondary" },
  { to: "/encyclopedia", icon: "ri-book-2-line", title: "百科", desc: "查词条、看知识解释", tone: "accent" },
  { to: "/hotsearch", icon: "ri-fire-line", title: "热门搜索", desc: "全网热搜榜单实时看", tone: "primary" },
] as const;

const toneClass: Record<string, string> = {
  primary: "bg-primary-100 text-primary-700",
  secondary: "bg-secondary-100 text-secondary-900",
  accent: "bg-accent-100 text-accent-900",
};

export default function Home() {
  useDocumentMeta({
    title: "百度一下 - 免费综合信息搜索与导航平台",
    description:
      "百度一下网站为用户提供便捷的全网信息搜索服务，覆盖新闻、视频、百科等多领域内容，支持快速找到所需信息和资源。",
    keywords: "百度一下,综合搜索,信息检索,网络导航,热门搜索,实时资讯,全网查询",
  });

  const topNews = contentItems
    .filter((i) => i.type === "news")
    .sort((a, b) => b.popularity - a.popularity)
    .slice(0, 3);
  const topVideos = contentItems
    .filter((i) => i.type === "video")
    .sort((a, b) => b.popularity - a.popularity)
    .slice(0, 3);

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <SiteHeader />

      <main className="flex-1">
        {/* Hero */}
        <section className="border-b border-background-200 bg-background-100">
          <div className="w-full px-4 pb-14 pt-14 md:px-6 md:pb-20 md:pt-20">
            <div className="mx-auto flex max-w-3xl flex-col items-center text-center">
              <h1 className="font-heading text-4xl font-black tracking-tight text-foreground-950 md:text-6xl">
                百度一下
              </h1>
              <p className="mt-4 max-w-xl text-sm leading-relaxed text-foreground-600 md:text-base">
                一个搜索框，聚合新闻、视频、百科等全网内容，高效精准地找到你需要的答案与资源。
              </p>

              <div className="mt-8 flex w-full justify-center">
                <SearchBar size="lg" autoFocus placeholder="输入关键词，搜索新闻 / 视频 / 百科" />
              </div>

              <div className="mt-6 w-full">
                <p className="mb-3 flex items-center justify-center gap-1.5 text-xs text-foreground-500">
                  <i className="ri-fire-line text-accent-500" />
                  热门搜索
                </p>
                <div className="flex flex-wrap justify-center gap-2">
                  {homeHotWords.map((word) => (
                    <Link
                      key={word}
                      to={`/search?q=${encodeURIComponent(word)}`}
                      className="rounded-full border border-background-300 bg-background-50 px-3.5 py-1.5 text-sm text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-600"
                    >
                      {word}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Channels */}
        <section className="w-full px-4 py-12 md:px-6 md:py-16">
          <div className="mx-auto max-w-6xl">
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              内容频道
            </h2>
            <p className="mt-2 text-sm text-foreground-600">按频道浏览，快速进入你关心的内容领域。</p>

            <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {channels.map((c) => (
                <Link
                  key={c.to}
                  to={c.to}
                  className="group flex flex-col rounded-lg border border-background-200 bg-background-50 p-5 transition-colors hover:border-primary-300"
                >
                  <span
                    className={`flex h-11 w-11 items-center justify-center rounded-lg ${toneClass[c.tone]}`}
                  >
                    <i className={`${c.icon} text-xl`} />
                  </span>
                  <h3 className="mt-4 flex items-center gap-1 font-heading text-base font-bold text-foreground-950 transition-colors group-hover:text-primary-600">
                    {c.title}
                    <i className="ri-arrow-right-line text-sm opacity-0 transition-opacity group-hover:opacity-100" />
                  </h3>
                  <p className="mt-1.5 text-sm text-foreground-600">{c.desc}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Intro */}
        <section className="border-y border-background-200 bg-background-100">
          <div className="mx-auto grid max-w-6xl grid-cols-1 gap-8 px-4 py-12 md:grid-cols-2 md:px-6 md:py-16">
            <div>
              <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                关于这个平台
              </h2>
              <p className="mt-4 text-sm leading-relaxed text-foreground-600">
                百度一下是一款免费的综合信息搜索与导航平台，面向普通网民、学生与职场人士，帮助你在一个入口内完成新闻浏览、视频查看与百科查询。
              </p>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                我们强调信息层级的清晰与内容的可读性，用简洁的界面把最需要的结果放在最前面。
              </p>
              <p className="mt-4 inline-flex rounded-md bg-accent-100 px-3 py-1.5 text-xs text-accent-900">
                当前为演示范围，搜索与筛选基于前端示例数据，待接入后端支持
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4 self-center">
              {[
                { num: String(contentItems.length), label: "演示内容条目" },
                { num: "3", label: "内容频道" },
                { num: "18", label: "热门搜索词" },
                { num: "100%", label: "响应式适配" },
              ].map((s) => (
                <div
                  key={s.label}
                  className="rounded-lg border border-background-200 bg-background-50 p-5"
                >
                  <p className="font-heading text-2xl font-black text-primary-600 md:text-3xl">
                    {s.num}
                  </p>
                  <p className="mt-1 text-xs text-foreground-600">{s.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Featured news */}
        <section className="w-full px-4 py-12 md:px-6 md:py-16">
          <div className="mx-auto max-w-6xl">
            <div className="mb-6 flex items-center justify-between">
              <div>
                <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                  精选资讯
                </h2>
                <p className="mt-1 text-sm text-foreground-600">根据热度为你推荐的最新内容</p>
              </div>
              <Link
                to="/news"
                className="hidden items-center gap-1 text-sm text-primary-600 whitespace-nowrap hover:text-primary-700 sm:flex"
              >
                更多新闻 <i className="ri-arrow-right-s-line" />
              </Link>
            </div>
            <div className="flex flex-col gap-4">
              {topNews.map((item) => (
                <ContentListItem key={item.id} item={item} />
              ))}
            </div>
          </div>
        </section>

        {/* Featured videos */}
        <section className="border-t border-background-200 bg-background-100">
          <div className="mx-auto max-w-6xl px-4 py-12 md:px-6 md:py-16">
            <div className="mb-6 flex items-center justify-between">
              <div>
                <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                  热门视频
                </h2>
                <p className="mt-1 text-sm text-foreground-600">轻松看懂热门话题</p>
              </div>
              <Link
                to="/video"
                className="hidden items-center gap-1 text-sm text-primary-600 whitespace-nowrap hover:text-primary-700 sm:flex"
              >
                更多视频 <i className="ri-arrow-right-s-line" />
              </Link>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {topVideos.map((item) => (
                <VideoCard key={item.id} item={item} />
              ))}
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}